import React, { useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "../lib/api";
import { Bell, CheckSquare, Trash, CheckCircle, Warning, Target } from "@phosphor-icons/react";

const ICONS = {
  sl_hit: <Warning size={14} weight="bold" className="text-neg" />,
  target_hit: <Target size={14} weight="bold" className="text-pos" />,
  exit_signal: <Warning size={14} weight="bold" className="text-mod" />,
};

export default function AlertsBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/alerts");
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("alerts load failed:", e);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 60 * 1000);
    return () => clearInterval(id);
  }, [load]);

  useEffect(() => {
    function close(e) {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    }
    if (open) document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const markRead = async () => {
    try {
      await api.post("/alerts/mark-read");
      setUnread(0);
      setItems((is) => is.map((i) => ({ ...i, read: true })));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("mark-read failed:", e);
    }
  };

  const clearAll = async () => {
    try {
      await api.delete("/alerts");
      setItems([]);
      setUnread(0);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("clear-all failed:", e);
    }
  };

  const triggerCheck = async () => {
    try {
      await api.post("/alerts/check");
      setTimeout(load, 1500);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("alerts check failed:", e);
    }
  };

  return (
    <div className="relative" ref={boxRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="alerts-bell"
        title="Alerts"
        className="relative p-2 border border-subtle rounded-sm hover:bg-surface-hover text-muted-fg hover:text-primary-fg transition"
      >
        <Bell size={14} weight={unread > 0 ? "fill" : "regular"} />
        {unread > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 text-[9px] font-mono rounded-sm flex items-center justify-center"
            style={{ background: "hsl(var(--signal-negative))", color: "#fff" }}
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 mt-2 w-80 bg-surface border border-subtle rounded-sm shadow-xl z-30 overflow-hidden"
            data-testid="alerts-dropdown"
          >
            <div className="flex items-center justify-between px-3 py-2 border-b border-subtle">
              <div className="text-[10px] font-mono uppercase tracking-widest text-muted-fg">
                Alerts {items.length > 0 && <span className="text-primary-fg">· {items.length}</span>}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={triggerCheck}
                  title="Check now"
                  className="text-[10px] font-mono uppercase tracking-widest text-secondary-fg hover:text-primary-fg"
                >
                  check
                </button>
                <button onClick={markRead} title="Mark all read" className="p-1 text-muted-fg hover:text-primary-fg">
                  <CheckSquare size={12} />
                </button>
                <button onClick={clearAll} title="Clear all" className="p-1 text-muted-fg hover:text-neg">
                  <Trash size={12} />
                </button>
              </div>
            </div>
            <div className="max-h-80 overflow-y-auto">
              {items.length === 0 ? (
                <div className="p-6 text-center text-sm text-muted-fg">
                  <CheckCircle size={18} className="mx-auto mb-2 text-muted-fg" />
                  No alerts yet. You'll be notified when watchlist stocks hit SL / Target / exit rule.
                </div>
              ) : (
                items.map((a) => (
                  <div
                    key={a.alert_id || `${a.kind}-${a.symbol}-${a.created_at}`}
                    className={
                      "px-3 py-3 border-b border-subtle last:border-b-0 flex gap-2 " +
                      (a.read ? "opacity-70" : "bg-app")
                    }
                    data-testid={`alert-item-${a.kind}`}
                  >
                    <div className="shrink-0 mt-0.5">{ICONS[a.kind] || <Bell size={14} />}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-primary-fg leading-tight">{a.message}</div>
                      <div className="text-[10px] font-mono text-muted-fg mt-1">
                        {new Date(a.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
